<script setup lang="ts">
import { useId } from 'vue';

type FileProps = {
  modelValue: File | null;
  label: string;
};

defineProps<FileProps>();
const emit = defineEmits(['update:modelValue', 'change']);
const id = useId();

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  if (files) {
    const isMultiple = target.hasAttribute('multiple');
    const valueToEmit = isMultiple ? Array.from(files) : files[0] || null;
    emit('update:modelValue', valueToEmit);
    emit('change', event);
  }
  else {
    emit('update:modelValue', null);
  }
}
</script>

<template>
  <div class="file-input-container">
    <label
      :for="id"
      class="file-input-label"
    >
      {{ label }}
      <span v-if="!!$attrs.required" aria-hidden="true" class="required-star">*</span>
    </label>

    <input
      v-bind="$attrs"
      :id="id"
      type="file"
      :required="!!$attrs.required"
      :aria-required="!!$attrs.required"
      :aria-invalid="!!$attrs['aria-invalid']"
      :aria-describedby="id"
      class="file-input"
      @change="handleFileChange"
    >
  </div>
</template>

<style scoped>
.file-input-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.file-input-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #1f2937;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.required-star {
  color: var(--yb-color-error);
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  font-weight: 700;
}

.file-input {
  display: block;
  height: auto;
  line-height: normal;

  border: 1px solid #d1d5db;
  background-color: white;
  border-radius: 0.375rem;

  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #1f2937;
  cursor: pointer;

  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.file-input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--yb-color-primary);
  border-color: var(--yb-color-primary);
}

.file-input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background-color: #f3f4f6;
}

input[type='file']::file-selector-button {
  all: unset;
  background-color: var(--yb-color-light-primary);
  border: 1px solid var(--yb-color-primary);
  padding: 0.25rem 0.5rem;
  border-radius: var(--yb-radius-sm);
  margin-right: 0.5rem;
  cursor: pointer;
}
</style>
